import { useLazyQuery } from '@apollo/client'
import {
  Button,
  Hidden,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import Pagination from '@material-ui/lab/Pagination'
import { matchSorter } from 'match-sorter'
import React, { useEffect, useMemo, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import { CANCEL_POLICIES_QUERY } from '../../graphql/cancelPolicies'
import { getEntityCode, getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import cancelPolicyPagesRoleManagement from '../../staticRoleManagement/cancelPolicyRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import { CancelPolicy } from '../../types/cancelAndModifyPolicies'
import PoliciesFilters from './PoliciesFilters'
import { PoliciesTable } from './PoliciesTable'
import { PolicyTableMobile } from './PolicyTableMobile'
import useStyles from './styles/index.styles'

const CancelPoliciesList: React.FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [page, setPage] = useState(1)
  const [perPage] = useState(5)
  const [total, setTotal] = useState(10)
  const [autoCompleteProvider, setAutoCompleteProvider] = useState<any>()
  const [searchInputValue, setSearchInputValue] = useState<string>('')
  const [filteredCancelPolicies, setFilteredCancelPolicies] = useState<
    CancelPolicy[]
  >([])

  const [
    getCancelPolicyData,
    { data, loading: loadingForCancelPolicy, refetch }
  ] = useLazyQuery<{
    cancelPolicies: CancelPolicy[]
  }>(CANCEL_POLICIES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const isRoleProvider = getRoleFromStorage() === ROLE.PROVIDER

  useEffect(() => {
    if (isRoleProvider) {
      getCancelPolicyData({
        variables: {
          providerCode: getEntityCode()
        }
      })
    } else if (autoCompleteProvider) {
      getCancelPolicyData({
        variables: {
          providerCode: autoCompleteProvider.code
        }
      })
    } else {
      setFilteredCancelPolicies([])
    }
  }, [autoCompleteProvider])

  useEffect(() => {
    setPage(1)
    const fullpoliciesList = data?.cancelPolicies ? data?.cancelPolicies : []

    const newFilteredPoliciesList = matchSorter(
      fullpoliciesList,
      searchInputValue,
      {
        keys: ['basicDetails.name', 'basicDetails.description']
      }
    )

    if (autoCompleteProvider || isRoleProvider) {
      setFilteredCancelPolicies(newFilteredPoliciesList)
    } else {
      setFilteredCancelPolicies([])
    }

    setTotal(newFilteredPoliciesList.length)
  }, [searchInputValue, loadingForCancelPolicy, data])

  const numberOfPages = useMemo(() => Math.ceil(total / perPage), [total])

  const hasAccessToChangeProvider = cancelPolicyPagesRoleManagement
    .find(
      (item) => item.route === '/policies/(cancel-policies|amendment-policies)'
    )
    ?.sections?.PROVIDER_SELECTOR?.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  return (
    <div>
      <PageHeader title={getTranslate('Cancel Policies')} />
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate('Cancel Policies')}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          component={LocalizedLink}
          to={
            cancelPolicyPagesRoleManagement
              .find(
                (item) =>
                  item.route ===
                  '/policies/(cancel-policies|amendment-policies)'
              )
              ?.sections?.ADD_POLICY_BUTTON.rolesListWithWriteAccess?.includes(
                getRoleFromStorage()
              )
              ? '/policies/cancel-policies/create'
              : ''
          }>
          {getTranslate(`Add${isSmallScreen ? '' : ' Cancel Policy'}`)}
        </Button>
      </div>
      <PoliciesFilters
        searchInputValue={searchInputValue}
        setSearchInputValue={setSearchInputValue}
        hasAccessToChangeProvider={hasAccessToChangeProvider}
        autoCompleteProvider={autoCompleteProvider}
        setAutoCompleteProvider={setAutoCompleteProvider}
        loadingForCancelPolicy={loadingForCancelPolicy}
        isRoleProvider={isRoleProvider}
      />
      <Hidden smDown>
        <PoliciesTable
          data={filteredCancelPolicies.slice(
            (page - 1) * perPage,
            page * perPage
          )}
          refetch={refetch}
          loadingForCancelPolicy={loadingForCancelPolicy}
          providerCode={autoCompleteProvider && autoCompleteProvider.code}
        />
      </Hidden>
      <Hidden mdUp>
        <PolicyTableMobile
          data={filteredCancelPolicies.slice(
            (page - 1) * perPage,
            page * perPage
          )}
          loadingForCancelPolicy={loadingForCancelPolicy}
          providerCode={autoCompleteProvider && autoCompleteProvider.code}
        />
      </Hidden>
      {!autoCompleteProvider && !isRoleProvider && (
        <div className={classes.noTenantSelected}>
          <Typography variant="h2">
            {getTranslate('Select a provider to see its policies')}
          </Typography>
        </div>
      )}
      {(autoCompleteProvider || isRoleProvider) && (
        <div className={classes.paginationContainer}>
          <Pagination
            count={numberOfPages}
            color="primary"
            size={isSmallScreen ? 'small' : 'medium'}
            page={page}
            onChange={(_, value) => setPage(value)}
          />
        </div>
      )}
    </div>
  )
}

export default CancelPoliciesList
